import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import { jwtDecode } from "jwt-decode";
import useAuth from "./useAuth";
import { poolData, ensureProjectEnvReady } from "./UserPool";

// How often to check the token's expiry, and how far ahead of expiry to
// proactively refresh it. Runs regardless of which auth backend is active
// (Cognito/Keycloak/Firebase/Azure) since they all update poolData.accessToken.
const TOKEN_CHECK_INTERVAL_MS = 60 * 1000;
const TOKEN_REFRESH_BUFFER_MS = 2 * 60 * 1000;

function RequireAuth ({ children }) {
  const { accessToken, authed, refreshToken} = useAuth();
  const location = useLocation();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    poolData.refreshToken = refreshToken;
    poolData.accessToken = accessToken;
  }, [accessToken, refreshToken]);

  useEffect(() => {
    if (!authed || !refreshToken) return;

    const checkAndRefresh = () => {
      try {
        const tok = poolData.accessToken;
        if (!tok) return;
        const decoded = jwtDecode(tok);
        if (!decoded?.exp) return;
        const msUntilExpiry = decoded.exp * 1000 - Date.now();
        if (msUntilExpiry <= TOKEN_REFRESH_BUFFER_MS) {
          refreshToken();
        }
      } catch (_) { /* not a decodable JWT for this backend, or refresh unavailable */ }
    };

    checkAndRefresh();
    const intervalId = setInterval(checkAndRefresh, TOKEN_CHECK_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [authed, refreshToken]);

  useEffect(() => {
    let cancelled = false;

    async function prepare() {
      if (!authed) {
        setReady(false);
        return;
      }
      try {
        // Ensure we have a token
        try { refreshToken && refreshToken(); } catch (_) { /* */ }
        const ok = await ensureProjectEnvReady(poolData.accessToken || accessToken);
        if (!cancelled) setReady(!!ok);
      } catch (_) {
        if (!cancelled) setReady(true); // fail-open to avoid blocking UI
      }
    }

    prepare();
    return () => { cancelled = true; };
  }, [authed, accessToken, refreshToken]);

  if (authed !== true) {
    return <Navigate to="/checkLogin" replace state={{ path: location.pathname }} />;
  }

  // Block children until project/env are ready
  if (!ready) {
    return null; // lightweight gate; Suspense/loader handled at route level
  }

  return children;
}

RequireAuth.propTypes = {
  children: PropTypes.any,
  clientAssessment: PropTypes.any
};

export default RequireAuth;
